import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IContactRespSchema,
  IPaymentGateway,
  IPaymentMethodReq,
  IPaymentMethodReqUI,
  IPaymentMethodResp,
  PaymentMethodReqSchemaUI,
} from '@monetize/types/app';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { SetupIntentResult, Stripe, StripeElements } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { FunctionComponent as FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSetAtom } from 'jotai';
import { doCreatePaymentMethodByAccount } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetAccountContacts } from '../../../api/contactsService';
import { accountServiceQueryKeys } from '../../../api/queryKeysService';
import { getAccountPaymentMethodStripeCallbackRoute } from '../../../constants/routes';
import { useACL } from '../../../services/acl/acl';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import { useToast } from '../../../services/toast';
import { tempPaymentMethodState } from '../../../store/global.store';
import { filterInactiveContacts } from '../../../utils';
import { showMicrodepositVerificationToast } from '../../../utils/stripe';
import { ContactFormModal } from '../../Contacts/ContactFormModal';
import { ContactSelect } from '../../Contacts/ContactSelect';
import {
  MBox,
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
} from '../../Monetize';

interface StripePaymentMethodFormFieldsProps {
  children?: React.ReactNode;
  isOpen: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onClose: (paymentMethod?: IPaymentMethodResp) => void;
  accountId: string;
  reloadData?: null | (() => void);
  paymentGateway: IPaymentGateway;
  setupIntent: string;
}

export const StripePaymentMethodFormFields: FC<
  StripePaymentMethodFormFieldsProps
> = ({
  isOpen,
  onClose,
  accountId,
  isLoading,
  isDisabled,
  paymentGateway,
}: StripePaymentMethodFormFieldsProps) => {
  const [newContact, setNewContact] = useState<IContactRespSchema>();
  const [contacts, setContacts] = useState<IContactRespSchema[]>([]);
  const setTempPaymentMethod = useSetAtom(tempPaymentMethodState);
  const elements = useElements();
  const stripe = useStripe();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { canDo } = useACL();

  const { open: isContactModalOpen, onClose: onCloseContactModal } =
    useDisclosure();
  const { data: contactList } = useGetAccountContacts(
    {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      accountId: accountId!,
      config: { rows: 100, page: 0 },
    },
    {
      enabled: !!accountId,
      meta: { showErrorToast: true },
      select: (data) => data?.content || [],
    },
  );

  // Helper function to create payment method object from request data
  const createPaymentMethodRequest = (
    setupIntentId: string,
    requestData: IPaymentMethodReqUI,
  ): IPaymentMethodReq => {
    return {
      paymentToken: setupIntentId,
      paymentGatewayId: paymentGateway.id,
      billingDetails: {
        fullName: requestData.contact?.fullName || '',
        email: requestData.contact?.email,
        phone: requestData.contact?.phone,
        title: requestData.contact?.title,
        line1: requestData.contact?.address?.line1,
        line2: requestData.contact?.address?.line2,
        city: requestData.contact?.address?.city,
        state: requestData.contact?.address?.state,
        postalCode: requestData.contact?.address?.postalCode,
        country: requestData.contact?.address?.country,
      },
      paymentMethodName: requestData.paymentMethodName,
    };
  };

  // Helper function to save payment method and invalidate cache
  const savePaymentMethod = async (
    paymentMethodReq: IPaymentMethodReq,
  ): Promise<IPaymentMethodResp> => {
    const res = await doCreatePaymentMethodByAccount(
      accountId,
      paymentMethodReq,
    );
    queryClient.invalidateQueries({
      queryKey: [
        ...accountServiceQueryKeys.paymentMethods.paymentMethodList(accountId!),
      ],
    });
    return res;
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const { mutate: confirmStripeSetup, isPending: saveLoading } = useMutation({
    mutationFn: ({
      stripe,
      elements,
      requestData,
    }: {
      stripe: Stripe;
      elements: StripeElements;
      requestData: IPaymentMethodReqUI;
    }) => {
      return Promise.all([
        stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: getStripeCallbackUrl(),
          },
          redirect: 'if_required',
        }),
        requestData,
      ]);
    },
    onSuccess: async ([stripeSetupResult, requestData]: [
      stripeSetupResult: SetupIntentResult,
      requestData: IPaymentMethodReqUI,
    ]) => {
      if (stripeSetupResult.error) {
        addToast({
          summary: `Stripe Connection Failed`,
          detail:
            `${stripeSetupResult.error.message}` ||
            'There was an error connecting to Stripe.',
          severity: 'error',
        });
        Sentry.captureException(
          new Error(
            `Error connecting to Stripe: ${stripeSetupResult.error.message}`,
          ),
          {
            tags: {
              type: 'STRIPE',
            },
            extra: stripeSetupResult,
          },
        );
        return;
      }

      const { setupIntent } = stripeSetupResult;

      if (
        setupIntent.status === 'requires_action' &&
        setupIntent?.next_action
      ) {
        if (
          setupIntent.next_action.type === 'redirect_to_url' &&
          setupIntent.next_action.redirect_to_url?.url
        ) {
          window.location.href = setupIntent.next_action.redirect_to_url.url;
          return;
        } else if (
          setupIntent.next_action.type === 'verify_with_microdeposits'
        ) {
          const verificationUrl =
            setupIntent.next_action.verify_with_microdeposits
              ?.hosted_verification_url;

          showMicrodepositVerificationToast(addToast, verificationUrl);
          onClose();
          return;
        }
      }
      setTempPaymentMethod(null);
      const newPaymentMethod = createPaymentMethodRequest(
        setupIntent.id,
        requestData,
      );

      try {
        const res = await savePaymentMethod(newPaymentMethod);
        onClose(res);
      } catch (error) {
        handleApiErrorToast(error);
      }
    },
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IPaymentMethodReqUI>({
    resolver: zodResolver(PaymentMethodReqSchemaUI),
    mode: 'onChange',
    defaultValues: {},
  });

  const selectedContactId = getValues('contactId');

  useEffect(() => {
    let activeContacts: IContactRespSchema[] = [];
    if (contactList?.length) {
      activeContacts = filterInactiveContacts(
        [...contactList],
        selectedContactId!,
      );
    }
    setContacts([...activeContacts]);
    if (newContact) {
      setValue('contactId', newContact.id, { shouldValidate: true });
    }
  }, [contactList, newContact, setValue, selectedContactId]);

  const onSubmit = async (requestData: IPaymentMethodReqUI) => {
    if (!stripe || !elements) {
      handleApiErrorToast({
        response: {
          data: {
            message: 'There was a problem creating the payment method.',
          },
        },
      });
      return;
    }
    // Add selected contact to requestData
    requestData.contact = contacts.find(
      (contact) => contact.id === requestData.contactId,
    );
    // Store in localstorage so redirect code in PaymentMethodStripeCallback can get data back if Stripe decides to make the callback
    setTempPaymentMethod(requestData);
    confirmStripeSetup({ stripe, elements, requestData });
  };

  const onError = (errs: any, event: any) => {
    logger.error('submit error: ', errs);
    logger.error('submit error event: ', event);
  };
  const onSubmitForm = handleSubmit(onSubmit, onError);

  const getStripeCallbackUrl = () => {
    const url = getAccountPaymentMethodStripeCallbackRoute(
      accountId || '',
      paymentGateway?.id || '',
    );
    return `${window.location.origin}${url}`;
  };

  const onCloseContactModalHandle = (contact?: IContactRespSchema) => {
    contact && setNewContact(contact);
    onCloseContactModal();
  };
  const onClosePaymentMethodHandle = () => {
    onClose();
  };

  return (
    <>
      <form onSubmit={onSubmitForm}>
        <MCenterModal
          isOpen={isOpen}
          onClose={onClose}
          modalTitle="New Payment Method"
          size="lg"
          modalContentProps={{ 'data-testid': 'payment-method-form' } as any}
          closeOnEsc={false}
          closeOnOverlayClick={false}
          renderFooter={() => (
            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
            >
              <MButton
                onClick={onClosePaymentMethodHandle}
                variant="cancel"
                minW="auto"
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                isLoading={saveLoading || isLoading}
                isDisabled={saveLoading || isLoading}
                onClick={onSubmitForm}
                minW="auto"
              >
                Save
              </MButton>
            </MStack>
          )}
        >
          <MBox>
            <MGrid templateColumns="repeat(12, 1fr)" gap={2} mb={3}>
              <MGridItem colSpan={12} key="paymentMethodName">
                <MFormField
                  isDisabled={isDisabled}
                  error={errors.paymentMethodName}
                  label="Payment method name"
                  tooltip="This will be auto-generated if not provided."
                >
                  <Controller
                    name="paymentMethodName"
                    control={control}
                    render={({ field: { value, ...field } }) => (
                      <MInput value={value || ''} {...field} />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12} key="Contact">
                <MFormField
                  isDisabled={isDisabled}
                  error={errors.contactId}
                  label="Contact"
                  isRequired
                >
                  <Controller
                    name="contactId"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <ContactSelect
                        {...field}
                        showAddContacts={canDo([
                          ['account_contacts', 'create'],
                        ])}
                        accountId={accountId}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </MGrid>
            <PaymentElement
              options={{
                layout: {
                  type: 'accordion',
                  radios: true,
                  defaultCollapsed: false,
                  spacedAccordionItems: true,
                },
                terms: {
                  usBankAccount: 'never',
                  card: 'never',
                },
              }}
            />
          </MBox>
        </MCenterModal>
      </form>

      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={onCloseContactModalHandle}
        accountId={accountId!}
      />
    </>
  );
};
